import type { DrawnDynamic, DrawnStaff } from '@/music/notation/drawn';
import type { Song } from '@/music/song';
import { curveObstacles } from '@/music/notation/curveObstacles';
import { dynamicBounds } from '@/music/notation/dynamicBounds';
import { slurTAtX, slurVerticalBounds, type StaffSlurCurve } from './slurGeometry';

export interface StaffHairpin {
  si: number;
  key: string;
  x1: number;
  x2: number;
  y: number;
  open1: number;
  open2: number;
}

export function hairpinVerticalBounds(h: StaffHairpin) {
  const half = Math.max(h.open1, h.open2) + 0.065;
  return { top: h.y - half, bottom: h.y + half };
}

/** Place every wedge segment before staff, text, lyric and page spacing. */
export function staffHairpins(
  staff: DrawnStaff,
  hairpins: Song['hairpins'],
  curves: StaffSlurCurve[],
  dynamics: Map<DrawnDynamic, number>,
): StaffHairpin[] {
  const anchors = new Map<string, { si: number; x: number }>();
  staff.systems.forEach((sys, si) => {
    for (const m of sys.measures)
      for (const n of m.notes) anchors.set(n.sourceId, { si, x: m.x + n.x });
  });
  const boxes = staff.systems.map((sys) => [
    ...curveObstacles(sys.measures),
    ...sys.measures.flatMap((m) =>
      m.dynamics.map((d) => {
        const b = dynamicBounds(d),
          y = dynamics.get(d) ?? d.y;
        return {
          left: m.x + d.x + b.left,
          right: m.x + d.x + b.right,
          top: y + b.top,
          bottom: y + b.bottom,
        };
      }),
    ),
  ]);
  const result: StaffHairpin[] = [];
  (hairpins ?? []).forEach((hp, index) => {
    const a = anchors.get(hp.fromId),
      b = anchors.get(hp.toId);
    if (!a || !b || b.si < a.si) return;
    const count = b.si - a.si + 1;
    for (let si = a.si; si <= b.si; si++) {
      const x1 = si === a.si ? a.x : 1.5,
        x2 = si === b.si ? b.x : staff.systems[si].width - 0.1;
      if (x2 <= x1) continue;
      const start = (si - a.si) / count,
        end = (si - a.si + 1) / count;
      const open1 = 0.55 * (hp.kind === 'cresc' ? start : 1 - start),
        open2 = 0.55 * (hp.kind === 'cresc' ? end : 1 - end);
      const half = Math.max(open1, open2) + 0.065;
      const obstacles = boxes[si]
        .filter((ink) => ink.right >= x1 && ink.left <= x2)
        .map((ink) => ({ top: ink.top, bottom: ink.bottom }));
      for (const sc of curves) {
        if (sc.si !== si) continue;
        const left = Math.max(sc.c.ax1, x1),
          right = Math.min(sc.c.ax2, x2);
        if (right < left) continue;
        const b = slurVerticalBounds(sc.c, slurTAtX(sc.c, left), slurTAtX(sc.c, right));
        obstacles.push({ top: b.top - 0.07, bottom: b.bottom + 0.07 });
      }
      obstacles.sort((a, b) => a.bottom - b.bottom);
      let y = 6.4;
      for (const ink of obstacles) {
        if (ink.bottom < y - half - 0.4 || ink.top > y + half + 0.4) continue;
        y = Math.max(y, ink.bottom + 0.4 + half);
      }
      result.push({ si, key: `hairpin-${index}-${si}`, x1, x2, y, open1, open2 });
    }
  });
  return result;
}
